import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChartConfiguration, Plugin } from 'chart.js';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AssetTypeClassification, ReportsService, ServiceFrequencyReportDataDto } from 'src/shared/service-proxies/service-proxies';
import { PageHelperService } from '../helpers/page-helper.service';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import { CustomerSettings } from '../services/customer-settings-service';
import { SessionStorageService } from '../services/session-storage.service';
import { VehicleTypeHelper } from '../helpers/VehicleTypeHelper';

@Component({
  selector: 'app-service-frequency-report',
  templateUrl: './service-frequency-report.component.html',
  styleUrls: ['./service-frequency-report.component.scss'],
  standalone: false
})
export class ServiceFrequencyReportComponent extends PagebaseComponent implements OnInit, OnDestroy {

  // Private variables (matches integration report style)
  customerId: string;
  data: ServiceFrequencyReportDataDto;

  private apiCall: Subscription;
  private filterChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Public variables
  loading: boolean = true;

  // filters (match PBIX slicers)
  selectedVehicleType: string;
  selectedContainerTypes: string[] = [];
  selectedLocationTypes: string[] = [];
  selectedDepotIds: string[] = [];

  // KPI
  averageDaysOnSite = 0;

  // Pie (slow vs not)
  pieLabels: string[] = ['Not slow moving', 'Slow moving'];
  pieColors: string[] = ['#4f86f7', '#e85c5c'];
  pieOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    rotation: Math.PI,
    plugins: {
      legend: { reverse: true }
    }
  };
  piePlugins: Plugin<'pie'>[] = [{
    id: 'sliceLabels',
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx;
      const dataset = chart.data.datasets[0];
      if (!dataset || !dataset.data) {
        return;
      }

      const values = dataset.data as number[];
      const total = values.reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
      if (!total) {
        return;
      }

      const meta = chart.getDatasetMeta(0);
      const fontSize = 12;
      const lineHeight = 14;
      ctx.save();
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#111';

      meta.data.forEach((arc, index) => {
        const value = values[index] ?? 0;
        if (!value) {
          return;
        }

        const startAngle = (arc as any).startAngle as number;
        const endAngle = (arc as any).endAngle as number;
        const innerRadius = (arc as any).innerRadius as number;
        const outerRadius = (arc as any).outerRadius as number;
        const angle = (startAngle + endAngle) / 2;
        const radius = (innerRadius + outerRadius) / 2;
        const x = (arc as any).x + Math.cos(angle) * radius;
        const y = (arc as any).y + Math.sin(angle) * radius;

        const percent = Math.round((value / total) * 100);
        ctx.fillText(`${value}`, x, y - (lineHeight / 2));
        ctx.fillText(`(${percent}%)`, x, y + (lineHeight / 2));
      });

      ctx.restore();
    }
  }];
  pieData: ChartConfiguration<'pie'>['data'] = {
    labels: this.pieLabels,
    datasets: [{ data: [0, 0], backgroundColor: this.pieColors }]
  };

  // Line (trend)
  lineData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [{ data: [], label: 'Slow moving fleet' }]
  };

  lineOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    plugins: { legend: { display: false } },
    layout: { padding: { top: 28 } },
    scales: { x: { display: true }, y: { display: true } }
  };

  linePlugins: Plugin<'line'>[] = [{
    id: 'pointLabels',
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx;
      const dataset = chart.data.datasets[0];
      if (!dataset || !dataset.data) {
        return;
      }

      const values = dataset.data as number[];
      const meta = chart.getDatasetMeta(0);
      const fontSize = 11;
      const lineHeight = 12;
      ctx.save();
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = '#111';

      meta.data.forEach((point, index) => {
        const value = values[index];
        if (value == null) {
          return;
        }

        const x = (point as any).x as number;
        const y = (point as any).y as number;
        const chartTop = chart.chartArea?.top ?? 0;
        const labelY = Math.max(y - lineHeight, chartTop + 2);
        ctx.fillText(`${value}`, x, labelY);
      });

      ctx.restore();
    }
  }];

  displayedColumns = ['containerId', 'assetType', 'depotName', 'locationName', 'from', 'to', 'info', 'days'];
  sortState: { active: string; direction: 'asc' | 'desc' } | null = null;

  constructor(
    private _reportsService: ReportsService,
    private sessionStorageService: SessionStorageService,
    pageHelperService: PageHelperService,
    applicationStatusService: ApplicationStatusService,
    customerSettings: CustomerSettings
  ) {
    super(pageHelperService, applicationStatusService, customerSettings, 'Service Frequency Report');
  }

  ngOnInit(): void {
    this.filterChange$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.getData());
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.filterChange$.complete();
  }

  unsubscribe(): void {
    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  reset(): void {
    this.data = null;
    this.averageDaysOnSite = 0;

    // reset charts to empty
    this.pieData = { labels: this.pieLabels, datasets: [{ data: [0, 0], backgroundColor: this.pieColors }] };
    this.pieOptions = { ...this.pieOptions, rotation: Math.PI };
    this.lineData = { labels: [], datasets: [{ data: [], label: 'Slow moving fleet' }] };

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }
  }

  private resetFiltersForCustomerChange(): void {
    this.selectedContainerTypes = [];
    this.selectedLocationTypes = [];
    this.selectedDepotIds = [];
  }

  // Get the view data (integration-report style)
  getData(): void {

    // If customer has changed, reset filters (requested behaviour)
    if (this.customerId != this.pageHelperService.selectedCompanyId) {
      this.customerId = this.pageHelperService.selectedCompanyId;
      this.resetFiltersForCustomerChange();
    }

    if (!this.customerId || this.customerId == "00000000-0000-0000-0000-000000000000") return;

    const storedVehicleType = this.sessionStorageService.getItem('dashboard-active-selectedVehicleType');
    if (storedVehicleType) {
      this.selectedVehicleType = JSON.parse(storedVehicleType);
    } else {
      this.selectedVehicleType = null;
    }

    // UI loading flag (same as integration report)
    this.loading = true;
    this.setIsLoading(this.loading);

    // cancel previous call if needed
    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

    this.apiCall = this._reportsService
      .getServiceFrequency(
        this.customerId,
        this.getAssetTypeClassification(this.selectedVehicleType),
        [],
        null,
        this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        null,
        this.selectedLocationTypes.length ? this.selectedLocationTypes : [],
        null,
        this.selectedDepotIds.length ? this.selectedDepotIds : []
      )
      .subscribe(data => {
        this.data = data;
        this.applySort();

        // KPI
        this.averageDaysOnSite = this.data?.averageDaysOnSite ?? 0;

        // pie
        this.pieData = {
          labels: this.pieLabels,
          datasets: [{
            data: [
              this.data?.summary?.notSlowMovingCount ?? 0,
              this.data?.summary?.slowMovingCount ?? 0
            ],
            backgroundColor: this.pieColors
          }]
        };
        this.pieOptions = { ...this.pieOptions, rotation: Math.PI };

        // line (proxy dates may be ISO strings; normalize through moment)
        const labels = (this.data?.trend ?? []).map(p =>
          p.cutOffDate ? moment(p.cutOffDate).format('DD/MM/YY') : ''
        );
        const values = (this.data?.trend ?? []).map(p => p.slowMoverCount ?? 0);

        this.lineData = {
          labels,
          datasets: [{
            data: values,
            label: 'Slow moving fleet',
            pointRadius: 4,
            pointHoverRadius: 5
          }]
        };

        this.loading = false;
        this.setIsLoading(this.loading);
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  onFilterChange(): void {
    this.filterChange$.next();
  }

  selectedVehicleTypeValueChange(newValue: string): void {
    if (newValue) {
      this.sessionStorageService.setItem('dashboard-active-selectedVehicleType', JSON.stringify(newValue));
    } else {
      this.sessionStorageService.removeItem('dashboard-active-selectedVehicleType');
    }

    this.selectedVehicleType = newValue;
    this.onFilterChange();
  }

  exportServiceFrequency(): void {
    this.loading = true;
    this.setIsLoading(this.loading);

    this.customerId = this.pageHelperService.selectedCompanyId;
    if (!this.customerId) {
      this.loading = false;
      this.setIsLoading(this.loading);
      return;
    }

    if (this.apiCall) {
      this.apiCall.unsubscribe();
    }

    this.apiCall = this._reportsService
      .exportServiceFrequency({
        customerId: this.customerId,
        group: this.getAssetTypeClassification(this.selectedVehicleType),
        selectedGroups: [],
        containerType: null,
        selectedContainerTypes: this.selectedContainerTypes.length ? this.selectedContainerTypes : [],
        locationType: null,
        selectedLocationTypes: this.selectedLocationTypes.length ? this.selectedLocationTypes : [],
        depotId: null,
        selectedDepotIds: this.selectedDepotIds.length ? this.selectedDepotIds : []
      })
      .subscribe(data => {
        this.loading = false;
        this.setIsLoading(this.loading);
        const filename = data.fileName;
        const blob: Blob = data.data;
        const a = document.createElement('a');
        a.download = filename;
        a.href = window.URL.createObjectURL(blob);
        a.click();
      }, error => {
        this.loading = false;
        this.setIsLoading(this.loading);
        console.error(error);
      });
  }

  toggleSort(column: string): void {
    if (!this.sortState || this.sortState.active !== column) {
      this.sortState = { active: column, direction: 'asc' };
    } else if (this.sortState.direction === 'asc') {
      this.sortState = { active: column, direction: 'desc' };
    } else {
      this.sortState = null;
    }

    this.applySort();
  }

  getSortDirection(column: string): 'asc' | 'desc' | null {
    if (!this.sortState || this.sortState.active !== column) {
      return null;
    }
    return this.sortState.direction;
  }

  private applySort(): void {
    if (!this.data?.rows?.length) {
      return;
    }

    if (!this.sortState) {
      return;
    }

    const { active, direction } = this.sortState;
    const multiplier = direction === 'asc' ? 1 : -1;

    this.data.rows = [...this.data.rows].sort((a, b) => {
      const left = this.getSortValue(a, active);
      const right = this.getSortValue(b, active);

      if (left == null && right == null) {
        return 0;
      }
      if (left == null) {
        return 1;
      }
      if (right == null) {
        return -1;
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * multiplier;
      }

      const leftString = String(left).toLowerCase();
      const rightString = String(right).toLowerCase();
      if (leftString < rightString) {
        return -1 * multiplier;
      }
      if (leftString > rightString) {
        return 1 * multiplier;
      }
      return 0;
    });
  }

  private getSortValue(row: any, column: string): string | number | null {
    switch (column) {
      case 'containerId':
        return row.containerId ?? null;
      case 'assetType':
        return row.assetType ?? null;
      case 'depotName':
        return row.depotName ?? null;
      case 'locationName':
        return row.locationName ?? null;
      case 'from':
        return row.from ? new Date(row.from).getTime() : null;
      case 'to':
        return row.to ? new Date(row.to).getTime() : null;
      case 'info':
        return row.info ?? null;
      case 'days':
        return row.days ?? null;
      default:
        return null;
    }
  }

  getDaysClass(days: number): string {
    if (days == null) {
      return 'days--none';
    }
    if (days >= 100) {
      return 'days--critical';
    }
    if (days >= 60) {
      return 'days--high';
    }
    if (days >= 28) {
      return 'days--warn';
    }
    return 'days--ok';
  }

  private getAssetTypeClassification(selectedVehicleType: string): AssetTypeClassification {
    return !selectedVehicleType ? AssetTypeClassification.All : VehicleTypeHelper.ConvertVehicleTypeNameToAssetTypeClassification(selectedVehicleType);
  }
}
